// Body of the JobModal. Three tabs:
//   - Details — job form + spool table (with per-spool stage expander)
//   - Drawings — PDF upload + auto-matched spool refs
//   - Conversation — plain-text thread
// Tabs 2 and 3 are gated on the job being persisted (need a job id
// to attach drawings/messages to); the labels show but the panes
// nudge the user to save first.
import { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useAuth } from "../../../../auth/useAuth";
import type {
  DrawingDto,
  JobDto,
  SolutionTypeDto,
  SpoolDto,
  SpoolStageDto,
  StageKey,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { JobFormFields, type JobFormState } from "./JobFormFields";
import type { SpoolDraft } from "../components/SpoolTable";
import { DrawingsTab } from "./DrawingsTab";
import { ConversationTab } from "./ConversationTab";
import { SpoolsTabPanel } from "./SpoolsTabPanel";
import { autoScheduleJob, listDrawings } from "../hooks/useProductionApi";
import { useActiveStages } from "../components/StagesProvider";
import { inferWeldBayKey, jobProgress, jobActiveStages, jobStageProgress } from "../lib/stageHelpers";
import { JobStageCard } from "../components/JobStageCard";

type Tab = "details" | "spools" | "jobstages" | "drawings" | "conversation";

interface Props {
  form: JobFormState;
  onFormChange(patch: Partial<JobFormState>): void;
  spools: SpoolDraft[];
  onSpoolsChange(next: SpoolDraft[]): void;
  // Append a single server-created spool (drawing candidate promote
  // path). Distinct from onSpoolsChange so the parent can use a
  // functional setState and dedupe by id — promoting multiple
  // candidates back-to-back via the bulk "Create Spools" button
  // queues several callbacks in the same render cycle, and a
  // closure-over-spools would drop all but the last append.
  onAppendSpool(spool: SpoolDto): void;
  team: TeamMemberDto[];
  types: SolutionTypeDto[];
  onManageTypes(): void;
  onImport(): void;
  // Simple View: the job-level stage draft + setter. Edited on the
  // Job Stages tab, persisted on Save by the parent.
  jobStages: SpoolStageDto[];
  onJobStagesChange(next: SpoolStageDto[]): void;
  onSuggestDueDate(date: string): void;
  error: string | null;
  // Set when the job is persisted — enables stages, drawings,
  // conversation, and the bulk-print menus.
  job?: JobDto | null;
  focusSpoolId?: number | null;
  onAutoScheduled?: () => void;
}

export function JobModalBody({
  form,
  onFormChange,
  spools,
  onSpoolsChange,
  onAppendSpool,
  team,
  types,
  onManageTypes,
  onImport,
  jobStages,
  onJobStagesChange,
  onSuggestDueDate,
  error,
  job,
  focusSpoolId,
  onAutoScheduled,
}: Props) {
  const [tab, setTab] = useState<Tab>("details");
  const { user } = useAuth();
  const stagesBySpool = useStagesBySpool(job?.spools);
  const weldBayBySpool = useWeldBayBySpool(job?.spools, job?.bay ?? null);
  const stageCatalogue = useActiveStages();
  // Simple mode: progress is the job-level pipeline. Detailed:
  // rolled up across spool pipelines.
  const progress = form.simpleMode
    ? jobStageProgress(jobStages)
    : jobProgress({
        spools: job?.spools ?? [],
        catalogue: stageCatalogue,
        weldBayBySpool,
      });
  const persisted = typeof job?.id === "number";
  const showProgress = persisted || (form.simpleMode && jobStages.length > 0);

  // Auto-switch tabs on a mode flip so the user never lands on a
  // hidden tab. Spools ↔ Job Stages.
  useEffect(() => {
    if (form.simpleMode && tab === "spools") setTab("jobstages");
    if (!form.simpleMode && tab === "jobstages") setTab("details");
  }, [form.simpleMode, tab]);
  // Internal cross-nav focus IDs. The prop-level focusSpoolId carries
  // QR deep links from outside the modal; openSpoolFocus carries
  // clicks on the "open spool" affordance inside the Drawings tab.
  // Drawings clicks ALSO expand the spool's stage panel, so the same
  // value flows into focusSpoolId AND expandSpoolId on SpoolTable.
  const [openSpoolFocus, setOpenSpoolFocus] = useState<number | null>(null);
  const effectiveSpoolFocus = openSpoolFocus ?? focusSpoolId ?? null;

  // Drawings list lifted up so the Spools tab can render a per-spool
  // "📐 Drawings" button that opens the linked PDF directly. Workshop
  // convention is 1:1 (one drawing per spool), so picking the first
  // match per spool is enough. Error state lives here too so initial
  // load + poll-refresh failures surface in the Drawings tab banner.
  const jobId = job?.id;
  const [drawings, setDrawings] = useState<DrawingDto[]>([]);
  const [drawingsError, setDrawingsError] = useState<string | null>(null);
  const refreshDrawings = useCallback(async () => {
    if (typeof jobId !== "number") {
      setDrawings([]);
      setDrawingsError(null);
      return;
    }
    try {
      const res = await listDrawings(jobId);
      setDrawings(res.drawings);
      setDrawingsError(null);
    } catch (e) {
      setDrawingsError(e instanceof Error ? e.message : String(e));
    }
  }, [jobId]);
  useEffect(() => { void refreshDrawings(); }, [refreshDrawings]);

  const drawingBySpoolId = useMemo(() => {
    const m = new Map<number, DrawingDto>();
    for (const d of drawings) {
      for (const match of d.matches) {
        if (!m.has(match.spoolId)) m.set(match.spoolId, d);
      }
    }
    return m;
  }, [drawings]);

  // QR deep links land here with a focus spool id; switch straight
  // to the Spools tab so the scroll-into-view in SpoolTable hits a
  // rendered row.
  useEffect(() => {
    if (focusSpoolId) setTab("spools");
  }, [focusSpoolId]);
  useEffect(() => {
    if (openSpoolFocus !== null) setTab("spools");
  }, [openSpoolFocus]);

  return (
    <div className="modal-body">
      {error && (
        <div style={{ background: "var(--bsp-red-tint)", color: "var(--bsp-red-fg)", padding: 10, borderRadius: 4, marginBottom: 14, fontSize: 12 }}>
          {error}
        </div>
      )}
      {showProgress && (
        <JobProgressBar
          pct={progress.pct}
          complete={progress.complete}
          total={progress.total}
          label={form.simpleMode ? "job stages" : "stages"}
        />
      )}
      <TabStrip
        active={tab}
        onChange={setTab}
        simpleMode={form.simpleMode}
        spoolsCount={job?.spools.length ?? 0}
        jobStagesCount={jobActiveStages(jobStages).length}
        drawingsCount={job?.drawingsCount ?? 0}
        messagesCount={job?.messagesCount ?? 0}
        disabledOthers={!persisted}
      />
      {tab === "details" && (
        <JobFormFields
          value={form}
          onChange={onFormChange}
          team={team}
          types={types}
          onManageTypes={onManageTypes}
          isExistingJob={persisted}
        />
      )}
      {tab === "jobstages" && form.simpleMode && (
        <JobStageCard
          jobStages={jobStages}
          onChange={onJobStagesChange}
          team={team}
          jobBay={job?.bay ?? null}
          jobStartDate={form.startDate || null}
          onSuggestDueDate={onSuggestDueDate}
        />
      )}
      {tab === "spools" && !form.simpleMode && (
        <SpoolsTabPanel
          job={job}
          spools={spools}
          onSpoolsChange={onSpoolsChange}
          team={team}
          types={types}
          focusSpoolId={effectiveSpoolFocus}
          expandSpoolId={openSpoolFocus}
          stagesBySpool={stagesBySpool}
          weldBayBySpool={weldBayBySpool}
          drawingBySpoolId={drawingBySpoolId}
          onStageChanged={onAutoScheduled}
          onImport={onImport}
          jobStartDate={form.startDate ?? undefined}
          nominalStageHours={form.nominalStageHours}
          onAutoSchedule={persisted && typeof job?.id === "number"
            ? () => void runAutoSchedule(job.id, form.startDate ?? null, onAutoScheduled)
            : undefined}
          onOpenDrawings={() => setTab("drawings")}
        />
      )}
      {tab === "drawings" && (
        persisted && typeof job?.id === "number" ? (
          <DrawingsTab
            jobId={job.id}
            spools={job.spools}
            drawings={drawings}
            drawingsError={drawingsError}
            onRefreshDrawings={refreshDrawings}
            onSpoolsChanged={onAutoScheduled}
            onSpoolCreated={onAppendSpool}
            onOpenSpool={(spoolId) => {
              // Clicking the same spool chip twice would be a no-op
              // under React's state dedup (setOpenSpoolFocus(5) from
              // 5 doesn't re-render), and SpoolTable's flashedFor
              // ref then refuses to re-scroll. Commit null first via
              // flushSync so the focus → null → spoolId transition
              // re-arms the scroll-into-view on every click.
              flushSync(() => setOpenSpoolFocus(null));
              setOpenSpoolFocus(spoolId);
            }}
          />
        ) : (
          <SaveFirstPrompt label="Save the job to start uploading drawings." />
        )
      )}
      {tab === "conversation" && (
        persisted && typeof job?.id === "number" ? (
          <ConversationTab
            jobId={job.id}
            currentUserName={user?.displayName ?? user?.email ?? "Unknown"}
          />
        ) : (
          <SaveFirstPrompt label="Save the job to start a conversation." />
        )
      )}
    </div>
  );
}

function TabStrip({
  active,
  onChange,
  simpleMode,
  spoolsCount,
  jobStagesCount,
  drawingsCount,
  messagesCount,
  disabledOthers,
}: {
  active: Tab;
  onChange: (t: Tab) => void;
  simpleMode: boolean;
  spoolsCount: number;
  jobStagesCount: number;
  drawingsCount: number;
  messagesCount: number;
  disabledOthers: boolean;
}) {
  const tab = (key: Tab, label: string, count?: number) => {
    const isActive = active === key;
    const disabled = disabledOthers && key !== "details";
    return (
      <button
        type="button"
        onClick={() => !disabled && onChange(key)}
        disabled={disabled}
        title={disabled ? "Save the job first" : undefined}
        style={{
          padding: "7px 14px",
          background: isActive ? "var(--bsp-surface)" : "transparent",
          border: "1px solid var(--bsp-border)",
          borderBottom: isActive ? "1px solid var(--bsp-surface)" : "1px solid var(--bsp-border)",
          marginBottom: -1,
          marginRight: 4,
          borderRadius: "4px 4px 0 0",
          fontSize: 12,
          fontWeight: 700,
          color: disabled ? "var(--bsp-fg-subtle)" : isActive ? "var(--bsp-blue)" : "var(--bsp-fg)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {label}
        {count !== undefined && count > 0 && (
          <span style={{
            marginLeft: 6,
            fontSize: 10,
            background: isActive ? "var(--bsp-blue)" : "var(--bsp-fg-muted)",
            color: "#fff",
            padding: "1px 6px",
            borderRadius: 999,
            fontFamily: "var(--bsp-font-mono)",
          }}>{count}</span>
        )}
      </button>
    );
  };
  return (
    <div style={{ display: "flex", borderBottom: "1px solid var(--bsp-border)", marginBottom: 14 }}>
      {tab("details", "Details")}
      {simpleMode
        ? tab("jobstages", "Job Stages", jobStagesCount)
        : tab("spools", "Spools", spoolsCount)}
      {tab("drawings", "Drawings", drawingsCount)}
      {tab("conversation", "Conversation", messagesCount)}
    </div>
  );
}

function JobProgressBar({ pct, complete, total, label }: { pct: number; complete: number; total: number; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, padding: "8px 12px", background: "var(--bsp-surface-muted)", borderRadius: 4 }}>
      <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--bsp-fg-muted)" }}>
        Progress
      </span>
      <div style={{ flex: 1, height: 6, background: "var(--bsp-border)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "var(--bsp-green-fg, #2E7D32)" : "var(--bsp-blue)" }} />
      </div>
      <span style={{ fontFamily: "var(--bsp-font-mono)", fontSize: 11, fontWeight: 700, color: "var(--bsp-fg)" }}>
        {pct}%
      </span>
      <span style={{ fontFamily: "var(--bsp-font-mono)", fontSize: 11, color: "var(--bsp-fg-muted)" }}>
        · {complete}/{total} {label}
      </span>
    </div>
  );
}

function SaveFirstPrompt({ label }: { label: string }) {
  return (
    <div style={{ padding: 30, textAlign: "center", color: "var(--bsp-fg-muted)", fontSize: 13 }}>
      {label}
    </div>
  );
}

async function runAutoSchedule(
  jobId: number,
  startDate: string | null,
  onDone?: () => void,
): Promise<void> {
  const fromDate = startDate ?? new Date().toISOString().slice(0, 10);
  if (!window.confirm(`Auto-schedule every spool's stages starting ${fromDate}? Existing dates on complete stages are preserved.`)) return;
  try {
    await autoScheduleJob(jobId, fromDate);
    onDone?.();
  } catch (e) {
    window.alert(`Auto-schedule failed: ${e instanceof Error ? e.message : String(e)}`);
  }
}

function useStagesBySpool(spools: SpoolDto[] | undefined): Map<number, SpoolStageDto[]> {
  const map = new Map<number, SpoolStageDto[]>();
  if (!spools) return map;
  for (const s of spools) map.set(s.id, s.stages);
  return map;
}

// The pill strip collapses 8 welding bays into one pill; this picker
// chooses which bay to display by walking the stage rows in canonical
// order and returning the first weldbayN that has any data (non-zero
// hours, status set, or dates). Falls back to null so the strip shows
// weldbay1 as a placeholder the user can click.
// Chosen weld bay per spool, driving the BayPicker highlight + which
// bay row shows in the stage table. Delegates to inferWeldBayKey so
// this matches the swap (planBaySwap) + completion (isSpoolComplete)
// logic exactly — they all agree on which bay is "chosen". The
// earlier bespoke version here required a progress signal (hours /
// dates / non-pending status) before treating a bay as chosen,
// which meant selecting a bay before welding started never moved
// the highlight — the swap looked dead.
function useWeldBayBySpool(
  spools: SpoolDto[] | undefined,
  jobBay: number | null,
): Map<number, StageKey | null> {
  const map = new Map<number, StageKey | null>();
  if (!spools) return map;
  for (const sp of spools) {
    map.set(sp.id, inferWeldBayKey(sp.stages, jobBay));
  }
  return map;
}

